import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  useApprovePasswordStore,
  useCheckLimitStore,
  useGetAccountsStore,
  useGetCardsStore,
  useGetDictionariesStore,
  useNotificationStore,
  useTopupStore,
} from '@lib/store';
import { AmountUtils } from '@shared/utils';
import { RequestType } from '@shared/api/rest/models';
import type { GetCardsResponse } from '@shared/api/rest/misc';
import { ButtonV2, Constants, ErrorMessage, Input, ItemsType, Modal, Select } from '@shared/widgets';

interface IForm {
  debitAccount?: ItemsType;
  toCard?: ItemsType;
  amount?: number;
  currency?: ItemsType;
}
const DEFAULT_VALUES: IForm = { debitAccount: undefined, toCard: undefined, amount: undefined, currency: undefined };

interface TransferToCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCard?: GetCardsResponse;
}

export const TransferToCardModal = (props: TransferToCardModalProps) => {
  const accountStore = useGetAccountsStore();
  const cardStore = useGetCardsStore();
  const dictionariesStore = useGetDictionariesStore();
  const checkLimitStore = useCheckLimitStore();
  const topupStore = useTopupStore();
  const approvePasswordStore = useApprovePasswordStore();
  const notificationStore = useNotificationStore();

  const [isContinue, setIsContinue] = useState<boolean>(false);
  const [password, setPassword] = useState<string>('');
  const [selectedCurrency, setSelectedCurrency] = useState<{ value: string; label: string; leftLabel: string }[]>();
  const [amountNotification, setAmountNotification] = useState<boolean>(false);

  const { formState, control, handleSubmit, watch, setValue, reset } = useForm<IForm>({ defaultValues: DEFAULT_VALUES });
  const debitAccount = watch('debitAccount');
  const toCard = watch('toCard');
  const amount = watch('amount');
  const currency = watch('currency');

  useEffect(() => {
    const makeSelectCurrency = dictionariesStore.data?.Currency.filter(
      (item) => item.Name === debitAccount?.leftLabel || item.Name === toCard?.leftLabel,
    ).map((item) => ({ value: item.Id, label: item.Name, leftLabel: item.Name }));
    if (makeSelectCurrency && makeSelectCurrency.length > 0) setValue('currency', makeSelectCurrency[0]);
    setSelectedCurrency(makeSelectCurrency);
  }, [debitAccount, toCard]);

  const makeSelectCard = cardStore.data?.map((item) => ({ value: item.Id, label: item.Number, leftLabel: item.Currency }));
  const makeSelectAccount = accountStore.data?.map((item) => ({ value: item.Id, label: item.Currency && item.Amount, leftLabel: item.Currency }));

  const submitHandler = async (data: IForm) => {
    try {
      if (Number(debitAccount?.label) < amount!) {
        setAmountNotification(true);
        return;
      }

      const result = await checkLimitStore.checkLimit({
        accountIdFrom: data.debitAccount?.value,
        amount: data.amount,
        currency: data.currency?.value,
        requestType: RequestType.TransferToCard,
      });
      if (result.data?.LimitExceeded) {
        notificationStore.setError(result.data.Note);
        return;
      }

      await topupStore.post(
        { Account: data.debitAccount?.value, Amount: data.amount!, Currency: data.currency?.value },
        data.toCard?.value as string,
      );
      setIsContinue(true);
    } catch {
      setIsContinue(false);
    }
  };

  useEffect(() => {
    if (amountNotification) notificationStore.setError(Constants.AMOUNT_ERROR);
    setAmountNotification(false);
  }, [amountNotification]);

  const confirmModal = async () => {
    await approvePasswordStore.approvePassword({ RequestId: topupStore.data?.Id, Password: password });
    notificationStore.setSuccess(Constants.DEFAULT_SUCCESS_MESSAGE);
    props.onClose();
    setIsContinue(false);
    reset(DEFAULT_VALUES);
    setPassword('');
  };

  const onCloseModal = () => {
    props.onClose();
    setIsContinue(false);
    setPassword('');
  };

  const handleBackButtonClick = () => {
    setIsContinue(false);
    setPassword('');
  };

  const handleCancelClick = () => {
    props.onClose();
    reset(DEFAULT_VALUES);
  };

  useEffect(() => {
    if (props.selectedCard) {
      setValue('toCard', { label: props.selectedCard?.Number, leftLabel: props.selectedCard?.Currency, value: props.selectedCard?.Id });
      setValue('amount', props.selectedCard.InvoiceAmount > 0 ? props.selectedCard?.InvoiceAmount : undefined);
    } else {
      setValue('toCard', { label: makeSelectCard?.[0]?.label, leftLabel: makeSelectCard?.[0]?.leftLabel, value: makeSelectCard?.[0]?.value });
    }
  }, [props.selectedCard, props.isOpen]);

  return (
    <Modal isOpen={props.isOpen} onClose={onCloseModal} title='Transfer to Card'>
      {!isContinue ? (
        <form className='w-full' onSubmit={handleSubmit(submitHandler)}>
          <div className='flex flex-col gap-3'>
            <Controller
              name='debitAccount'
              control={control}
              defaultValue={makeSelectAccount?.[0]}
              rules={{ required: 'This field is required', maxLength: 100 }}
              render={({ field: { value, onChange } }) => (
                <div className='flex w-full flex-col gap-1'>
                  <div className='text-xs font-semibold text-[#929292] uppercase'>Debit Account</div>
                  <Select wrapperClassName='!p-0' items={makeSelectAccount} double value={value?.value} onChange={onChange} />
                </div>
              )}
            />
            <Controller
              name='toCard'
              control={control}
              rules={{ required: 'This field is required', maxLength: 100 }}
              render={({ field: { value, onChange } }) => (
                <div className='flex w-full flex-col gap-1'>
                  <div className='text-xs font-semibold text-[#929292] uppercase'>To Card</div>
                  <Select wrapperClassName='!p-0' items={makeSelectCard} double value={value?.value} onChange={onChange} />
                </div>
              )}
            />
            <Controller
              name='amount'
              control={control}
              rules={{ required: 'This field is required', maxLength: 100 }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <div className='flex w-full flex-col gap-1'>
                  <div className='text-xs font-semibold text-[#929292] uppercase'>Amount</div>
                  <Input
                    className='!h-[46px]'
                    variant='outlined'
                    rounded
                    type='number'
                    error={error?.message}
                    name='Amount'
                    placeholder='Enter Amount'
                    value={value}
                    onChange={(value) => AmountUtils.amountChange(value, onChange)}
                    required
                  />
                  <ErrorMessage text={error?.message} />
                </div>
              )}
            />
            {selectedCurrency && (
              <Controller
                name='currency'
                control={control}
                defaultValue={selectedCurrency?.[0]}
                rules={{ required: 'This field is required', maxLength: 100 }}
                render={({ field: { value, onChange } }) => (
                  <div className='flex w-full flex-col gap-1'>
                    <div className='text-xs font-semibold text-[#929292] uppercase'>Currency</div>
                    <Select wrapperClassName='!p-0' items={selectedCurrency} value={value?.value} onChange={onChange} />
                  </div>
                )}
              />
            )}
          </div>
          <div className='mt-6.5 flex items-center justify-between gap-2.5'>
            <ButtonV2 className='!h-[46px]' color='outlined' fullWidth onClick={handleCancelClick}>
              Cancel
            </ButtonV2>
            <ButtonV2 className='!h-[46px]' fullWidth type='submit' disabled={!amount || formState.isSubmitting}>
              Continue
            </ButtonV2>
          </div>
        </form>
      ) : (
        <div className='w-full'>
          <div className='mb-4'>
            <div>
              <div className='mb-1 text-xs font-semibold text-[#929292] uppercase'>Debit Account:</div>
              <div className='text-sm font-semibold text-[#fcfcfc] uppercase'>
                <div className='flex items-start justify-start'>
                  <div className='mr-5'>{debitAccount?.leftLabel}</div>
                  <div className='mr-5 whitespace-nowrap'>{AmountUtils.transformToAmount(debitAccount?.label as number)}</div>
                </div>
              </div>
            </div>
            <div>
              <div className='mb-1 text-xs font-semibold text-[#929292] uppercase'>To Card:</div>
              <div className='text-sm font-semibold text-[#fcfcfc] uppercase'>
                <div className='flex items-start justify-start'>
                  <div className='mr-5'>{toCard?.leftLabel}</div>
                  <div className='mr-5 whitespace-nowrap'>{toCard?.label}</div>
                </div>
              </div>
            </div>
            <div>
              <div className='mb-1 text-xs font-semibold text-[#929292] uppercase'>Amount:</div>
              <div className='text-sm font-semibold text-[#fcfcfc] uppercase'>
                <div className='flex items-start justify-start'>
                  <div className='mr-5'>{currency?.leftLabel}</div>
                  <div className='mr-5 whitespace-nowrap'>{AmountUtils.transformToAmount(amount!)}</div>
                </div>
              </div>
            </div>
          </div>
          <div className='mt-3'>
            <div className='mb-1 text-sm font-semibold text-[#666] uppercase'>To confirm Top Up please enter your password</div>
            <Input
              variant='outlined'
              rounded
              className='h-[48px]'
              placeholder='Password'
              name='Password'
              value={password}
              type='password'
              onChange={setPassword}
              required
            />
          </div>
          <div className='mt-4 flex w-full flex-row items-center justify-between gap-4'>
            <ButtonV2 color='outlined' fullWidth onClick={handleBackButtonClick}>
              Back
            </ButtonV2>
            <ButtonV2 color='primary' fullWidth disabled={password ? password.length < 1 : true} onClick={confirmModal}>
              Confirm
            </ButtonV2>
          </div>
        </div>
      )}
    </Modal>
  );
};
